"use client";

/**
 * O ✓ que se DESENHA. Pequeno, dentro de rótulo ("Salvo", "Copiado"); com
 * disco, no momento de sucesso (pagamento, assinatura, venda ganha), onde o
 * disco pousa com o único quique do sistema e um anel se desfaz uma vez.
 *
 * `vez` reinicia o desenho: mude o número pra desenhar de novo.
 */
import { m } from "motion/react";
import type { CSSProperties } from "react";
import { useMovimento } from "./provider";

export function MarcaDeOk({
  tamanho = 14,
  espessura = 2.4,
  disco = false,
  anel = false,
  vez = 0,
  className,
  style,
}: {
  tamanho?: number;
  espessura?: number;
  /** Disco cheio em `--ok` atrás do traço (momento de sucesso). */
  disco?: boolean;
  /** Anel que cresce e some uma vez (só junto do disco). */
  anel?: boolean;
  vez?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const mov = useMovimento();
  const s = mov.r.sucesso;
  const traco = (
    <m.svg
      key={`t${vez}`}
      width={disco ? tamanho * 0.5 : tamanho}
      height={disco ? tamanho * 0.5 : tamanho}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      style={{ display: "block", overflow: "visible" }}
    >
      <m.path
        d="M4.5 12.5l5 5L19.5 7"
        stroke="currentColor"
        strokeWidth={disco ? espessura * 1.2 : espessura}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={s.traco.initial}
        animate={s.traco.animate}
      />
    </m.svg>
  );

  if (!disco) {
    return (
      <span className={className} style={{ display: "inline-flex", ...style }}>
        {traco}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{ position: "relative", display: "inline-grid", placeItems: "center", width: tamanho, height: tamanho, ...style }}
    >
      {anel ? (
        <m.span
          key={`a${vez}`}
          aria-hidden
          initial={s.anel.initial}
          animate={s.anel.animate}
          style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid var(--ok)" }}
        />
      ) : null}
      <m.span
        key={`d${vez}`}
        initial={s.disco.initial}
        animate={s.disco.animate}
        style={{
          gridArea: "1 / 1",
          width: tamanho,
          height: tamanho,
          borderRadius: "50%",
          background: "var(--ok)",
          color: "var(--panel)",
          display: "grid",
          placeItems: "center",
        }}
      >
        {traco}
      </m.span>
    </span>
  );
}

/**
 * O par do ✓: o ✕ que se DESENHA (os dois traços, um depois do outro). Com
 * disco em `--bad`, no desfecho que falhou. Erro é firme: o disco pousa SEM
 * quique (curva de entrada, não a mola `pouso`).
 */
export function MarcaDeErro({
  tamanho = 14,
  espessura = 2.4,
  disco = false,
  vez = 0,
  className,
  style,
}: {
  tamanho?: number;
  espessura?: number;
  disco?: boolean;
  vez?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const mov = useMovimento();
  const lado = disco ? tamanho * 0.46 : tamanho;
  const traco = (d: string, atraso: number) => (
    <m.path
      d={d}
      stroke="currentColor"
      strokeWidth={disco ? espessura * 1.2 : espessura}
      strokeLinecap="round"
      initial={{ pathLength: mov.reduzido ? 1 : 0 }}
      animate={{ pathLength: 1, transition: mov.reduzido ? { duration: 0 } : mov.tempo("sm", "entrada", atraso) }}
    />
  );
  const x = (
    <m.svg key={`x${vez}`} width={lado} height={lado} viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block", overflow: "visible" }}>
      {traco("M6 6l12 12", 0.05)}
      {traco("M18 6L6 18", 0.15)}
    </m.svg>
  );
  if (!disco) {
    return (
      <span className={className} style={{ display: "inline-flex", ...style }}>
        {x}
      </span>
    );
  }
  const d = mov.reduzido
    ? { initial: { opacity: 0 }, animate: { opacity: 1, transition: mov.tempo("xs") } }
    : { initial: { opacity: 0, scale: 0.6 }, animate: { opacity: 1, scale: 1, transition: mov.tempo("md") } };
  return (
    <m.span
      key={`d${vez}`}
      className={className}
      initial={d.initial}
      animate={d.animate}
      style={{
        width: tamanho,
        height: tamanho,
        borderRadius: "50%",
        background: "var(--bad)",
        color: "var(--panel)",
        display: "inline-grid",
        placeItems: "center",
        ...style,
      }}
    >
      {x}
    </m.span>
  );
}
